import React, { useState } from "react";
import { Dialog, Box, Typography, Alert } from "@mui/material";
import { C, CloseIcon, outlineBtn, primaryBtn } from "./staffingUi";
import { OpeningsStep, MethodStep, LevelsStep, ReviewStep } from "./WizardSteps";
import {
  WIZARD_STEPS, wizardTotal, stepError, sessionLabel,
} from "./staffingModel";

/**
 * The four-step "Open to instructors" flow: which sessions, how many instructors per role, how they sign up, and
 * who may request. {@code initial} comes from startWizard; {@code onSubmit(state)} posts it and resolves when saved.
 */
export default function OpenToInstructorsDialog({
  session, sessionCount, rangeText, roles, initial, onClose, onSubmit,
}) {
  const [w, setW] = useState(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (patch) => { setW((prev) => ({ ...prev, ...patch })); setError(""); setServerError(""); };
  const goTo = (step) => { setW((prev) => ({ ...prev, step })); setError(""); };

  const next = () => {
    const message = stepError(w, w.step);
    if (message) { setError(message); return; }
    goTo(w.step + 1);
  };

  const post = async () => {
    setBusy(true);
    setServerError("");
    try {
      await onSubmit(w);
    } catch (e) {
      setServerError(e?.response?.data?.message || e?.message || "Could not save. Try again.");
      setBusy(false);
    }
  };

  const total = wizardTotal(w);
  const steps = {
    0: () => <OpeningsStep w={w} set={set} session={session} sessionCount={sessionCount} rangeText={rangeText}
      roles={roles} error={error} />,
    1: () => <MethodStep w={w} set={set} />,
    2: () => <LevelsStep w={w} set={set} error={error} />,
    3: () => <ReviewStep w={w} session={session} sessionCount={sessionCount} goTo={goTo} />,
  };
  const body = steps[w.step]();

  const title = `${w.edit ? "Edit sign-ups for " : "Open "}${w.scope === "all" ? "all sessions" : sessionLabel(session)}${w.edit ? "" : " to instructors"}`;

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth={false} aria-labelledby="open-to-instructors-title"
      PaperProps={{ "data-testid": "open-to-instructors-dialog",
        sx: { width: "min(620px, 100%)", m: "16px", maxHeight: "calc(100vh - 48px)", borderRadius: "16px",
          boxShadow: "var(--shadow-card-md)" } }}>
      <Box sx={{ p: "20px 24px 0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box component="span" sx={{ font: "600 11px var(--font-ui)", letterSpacing: ".5px", textTransform: "uppercase", color: C.muted }}>
            {w.edit ? "Edit sign-ups" : "Open to instructors"} · Step {w.step + 1} of {WIZARD_STEPS.length}
          </Box>
          <Box component="button" type="button" aria-label="Close" onClick={onClose} disabled={busy}
            sx={{ width: 36, height: 36, m: "-8px -8px 0 0", border: 0, borderRadius: "8px", bgcolor: "transparent",
              display: "grid", placeItems: "center", color: C.muted, cursor: "pointer", "&:hover": { bgcolor: C.hover, color: C.ink } }}>
            <CloseIcon />
          </Box>
        </Box>
        <Typography id="open-to-instructors-title" component="h2"
          sx={{ m: "4px 0 16px", fontSize: 20, fontWeight: 700, letterSpacing: "-.01em" }}>{title}</Typography>
        <Box aria-hidden="true" sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "6px", pb: "16px", borderBottom: `1px solid ${C.line}` }}>
          {WIZARD_STEPS.map((t, k) => (
            <Box key={t} component="span" sx={{ display: "grid", gap: "6px", fontSize: { xs: 0, sm: 12 }, fontWeight: k === w.step ? 600 : 500,
              color: k <= w.step ? C.ink : C.muted,
              "&::before": { content: '""', height: 3, borderRadius: "2px", bgcolor: k <= w.step ? C.primary : C.line } }}>{t}</Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: "20px 24px", overflow: "auto", flex: 1 }}>
        {body}
        {serverError && <Alert severity="error" sx={{ mt: 2, borderRadius: "12px" }}>{serverError}</Alert>}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", p: "14px 24px", borderTop: `1px solid ${C.line}`, bgcolor: "#fafafb" }}>
        <Box component="span" sx={{ mr: "auto", fontSize: 12.5, color: C.muted }}>
          {w.step === 0 ? `${total} opening${total === 1 ? "" : "s"}${w.scope === "all" ? " per session" : ""}` : ""}
        </Box>
        {w.step > 0
          ? <Box component="button" type="button" sx={outlineBtn} disabled={busy} onClick={() => goTo(w.step - 1)}>Back</Box>
          : <Box component="button" type="button" sx={{ ...outlineBtn, border: 0, bgcolor: "transparent" }} onClick={onClose}>Cancel</Box>}
        {w.step < WIZARD_STEPS.length - 1
          ? <Box component="button" type="button" sx={primaryBtn} onClick={next}>Continue</Box>
          : <Box component="button" type="button" sx={primaryBtn} disabled={busy} onClick={post}>
              {busy ? "Saving…" : w.edit ? "Save changes" : "Post opening"}
            </Box>}
      </Box>
    </Dialog>
  );
}
